// useLongPress — fires a callback after the user holds a press for `delay`ms.
// Cancels on move (so scrolling doesn't accidentally trigger), pointer-up, or leave.
// Returns event handlers ready to spread onto a JSX element.
//
// Behaviour:
//   - Triggers ONLY for primary mouse button or touch (ignores right-click).
//   - Triggers haptic feedback (navigator.vibrate) on touch devices.
//   - Sets `pressedRef` so the consumer's onClick can check `wasLongPress` and bail.
import { useCallback, useRef } from "react";

const DEFAULT_DELAY = 450;        // ms
const MOVE_TOLERANCE = 10;        // px before press is cancelled (user is scrolling)

export function useLongPress(onLongPress, { delay = DEFAULT_DELAY, haptic = true } = {}) {
  const timerRef = useRef(null);
  const startRef = useRef({ x: 0, y: 0 });
  const firedRef = useRef(false);

  const clear = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const start = useCallback(
    (e) => {
      // Ignore non-primary mouse buttons (e.g. right-click context menu)
      if (e.button !== undefined && e.button !== 0) return;
      const touch = e.touches?.[0] || e;
      startRef.current = { x: touch.clientX || 0, y: touch.clientY || 0 };
      firedRef.current = false;
      clear();
      timerRef.current = setTimeout(() => {
        firedRef.current = true;
        if (haptic && "vibrate" in navigator) {
          try { navigator.vibrate(15); } catch (_) { /* noop */ }
        }
        onLongPress(e);
      }, delay);
    },
    [onLongPress, delay, haptic, clear]
  );

  const move = useCallback((e) => {
    if (!timerRef.current) return;
    const touch = e.touches?.[0] || e;
    const dx = (touch.clientX || 0) - startRef.current.x;
    const dy = (touch.clientY || 0) - startRef.current.y;
    if (Math.hypot(dx, dy) > MOVE_TOLERANCE) clear();
  }, [clear]);

  const consumeFired = useCallback(() => {
    const f = firedRef.current;
    firedRef.current = false;
    return f;
  }, []);

  // Handlers — spread onto the target element.
  // Pointer events cover both mouse and touch on modern browsers; we add
  // touch fallbacks for older Safari versions.
  const handlers = {
    onPointerDown: start,
    onPointerMove: move,
    onPointerUp: clear,
    onPointerLeave: clear,
    onPointerCancel: clear,
    onTouchStart: start,
    onTouchMove: move,
    onTouchEnd: clear,
    onContextMenu: (e) => {
      // Prevent the iOS Safari "Open Image in New Tab" sheet from interrupting
      // the long-press; we already handle it ourselves.
      e.preventDefault();
    },
  };

  return { handlers, consumeFired };
}
